import React, {
    createContext,
    useContext,
    useState,
    useEffect,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useNavigate,
} from "react-router-dom";

import {
    createEquipment,
} from "../../../../features/equipment/equipmentActions";

import {
    clearEquipmentErrors,
    resetCreateEquipmentState,
} from "../../../../features/equipment/equipmentSlice";

// ============================================================
// CONTEXT
// ============================================================

export const FormContext = createContext(null);

// ============================================================
// INITIAL FORM DATA
// ============================================================

export const initialFormData = {

    // ============================================================
    // STEP 1 - Equipment Identity
    // ============================================================

    name: "",
    category: "",
    subCategory: "",
    brand: "",
    model: "",
    assetId: "",
    serialNumber: "",
    barcode: "",
    purchaseDate: "",

    // Photo
    photoFile: null,
    photoUrl: "",

    // ============================================================
    // STEP 2 - Location & Ownership
    // ============================================================

    location: "",
    specificArea: "",
    ownership: "",
    assignedTo: "",
    supplier: "",

    // ============================================================
    // STEP 3 - Operational Status & Criticality
    // ============================================================

    operationalStatus: "In Service",
    condition: "Good",
    riskLevel: "Medium",

    foodSafetyRelevance:
        "Yes - Affects food safety",

    dateInService: "",
    expectedLifespan: "",
    initialInspectionDate: "",

    // ============================================================
    // STEP 4 - Compliance Requirements
    // ============================================================

    compliance: {
        pat: true,
        iot: true,
        gas: false,
        statutory: false,
        calibration: false,
        fire: false,
    },

    // ============================================================
    // STEP 5 - Maintenance & Servicing
    // ============================================================

    requiresRoutineMaint: "Yes",

    maintenanceFrequency: "",

    nextMaintenanceDate: "",

    serviceProvider: "",

    contractReference: "",

    contractExpiryDate: "",

    maintenanceResponsibility: "",

    // ============================================================
    // STEP 6 - PAT / Electrical Testing
    // ============================================================

    requiresPat: "Yes",

    patFrequency: "",

    lastPatDate: "",

    nextPatDue: "",

    lastTestResult: "",

    // ============================================================
    // STEP 7 - IoT / Sensor Setup
    // ============================================================

    sensorInstalled: "Yes",

    sensorType: "",

    sensorId: "",

    alertThresholds: "",

    connectionStatus: "Live",

    // ============================================================
    // STEP 8 - Warranty & Document
    // ============================================================

    warrantyActive: "Yes",

    warrantyExpiryDate: "",

    warrantyProvider: "",

    warrantyReference: "",

    documentUrl: "",

    documentName: "",

    documentType: "",

    // ============================================================
    // STEP 9 - Additional Information
    // ============================================================

    notes: "",

    decommissioned: "No",

    decommissionDate: "",

    decommissionReason: "",
};

// ============================================================
// PROVIDER
// ============================================================

export const EquipmentFormProvider = ({
    children,
}) => {

    const dispatch = useDispatch();

    const navigate = useNavigate();

    // ============================================================
    // REDUX STATE
    // ============================================================

    const {
        creating,
        createSuccess,
        error,
    } = useSelector(
        (state) => state.equipment
    );

    // ============================================================
    // FORM STATE
    // ============================================================

    const [formData, setFormData] =
        useState({
            ...initialFormData,

            compliance: {
                ...initialFormData.compliance,
            },
        });

    // ============================================================
    // UPDATE NORMAL FIELD
    // ============================================================

    const updateField = (
        fieldName,
        value
    ) => {

        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }));

        if (error) {
            dispatch(
                clearEquipmentErrors()
            );
        }
    };

    // ============================================================
    // UPDATE COMPLIANCE FIELD
    // ============================================================

    const updateComplianceField = (
        fieldName,
        value
    ) => {

        setFormData((prev) => ({
            ...prev,

            compliance: {
                ...prev.compliance,
                [fieldName]: value,
            },
        }));

        if (error) {
            dispatch(
                clearEquipmentErrors()
            );
        }
    };

    // ============================================================
    // RESET FORM
    // ============================================================

    const resetForm = () => {

        setFormData({
            ...initialFormData,

            compliance: {
                ...initialFormData.compliance,
            },
        });
    };

    // ============================================================
    // FILL FAKE / TEST DATA
    // ============================================================

    const handleFillFakeData = () => {

        const uniqueId =
            Date.now()
                .toString()
                .slice(-6);

        dispatch(
            clearEquipmentErrors()
        );

        dispatch(
            resetCreateEquipmentState()
        );

        setFormData({

            // ====================================================
            // STEP 1
            // ====================================================

            name: "Walk-in Fridge",

            category: "Refrigeration",

            subCategory: "Walk-in Fridge",

            brand: "Samsung",

            model: "RF-9000",

            assetId: `REF-${uniqueId}`,

            serialNumber: `SN-${uniqueId}`,

            barcode: `BAR-${uniqueId}`,

            purchaseDate: "2026-01-15",

            photoFile: null,

            photoUrl: "",

            // ====================================================
            // STEP 2
            // ====================================================

            location: "Main Kitchen",

            specificArea:
                "Cold Storage Area",

            ownership: "Owned",

            assignedTo: "",

            supplier:
                "ABC Catering Equipment Ltd",

            // ====================================================
            // STEP 3
            // ====================================================

            operationalStatus:
                "In Service",

            condition: "Good",

            riskLevel: "Medium",

            foodSafetyRelevance:
                "Yes - Affects food safety",

            dateInService:
                "2026-02-01",

            expectedLifespan: "10",

            initialInspectionDate:
                "2026-02-01",

            // ====================================================
            // STEP 4
            // ====================================================

            compliance: {
                pat: true,
                iot: true,
                gas: false,
                statutory: true,
                calibration: true,
                fire: false,
            },

            // ====================================================
            // STEP 5
            // ====================================================

            requiresRoutineMaint:
                "Yes",

            maintenanceFrequency:
                "Monthly",

            nextMaintenanceDate:
                "2026-10-15",

            serviceProvider:
                "ABC Engineering Services",

            contractReference:
                `CONT-${uniqueId}`,

            contractExpiryDate:
                "2027-01-31",

            maintenanceResponsibility:
                "Contractor",

            // ====================================================
            // STEP 6
            // ====================================================

            requiresPat: "Yes",

            patFrequency: "Annual",

            lastPatDate: "2026-02-10",

            nextPatDue: "2027-02-10",

            lastTestResult: "Pass",

            // ====================================================
            // STEP 7
            // ====================================================

            sensorInstalled: "Yes",

            sensorType: "Temperature",

            sensorId:
                `SEN-${uniqueId}`,

            alertThresholds:
                "2°C to 8°C",

            connectionStatus: "Live",

            // ====================================================
            // STEP 8
            // ====================================================

            warrantyActive: "Yes",

            warrantyExpiryDate:
                "2028-01-15",

            warrantyProvider:
                "Samsung",

            warrantyReference:
                `WAR-${uniqueId}`,

            documentUrl: "",

            documentName: "",

            documentType: "",

            // ====================================================
            // STEP 9
            // ====================================================

            notes:
                "Test equipment generated for development. Located in the main kitchen cold storage area.",

            decommissioned: "No",

            decommissionDate: "",

            decommissionReason: "",
        });
    };

    // ============================================================
    // SUBMIT EQUIPMENT
    // ============================================================

    const handleSubmit = async (e) => {

        e.preventDefault();

        dispatch(
            clearEquipmentErrors()
        );

        // ==========================================================
        // REQUIRED FIELD VALIDATION
        // ==========================================================

        if (!formData.name.trim()) {

            alert(
                "Equipment Name is required."
            );

            return;
        }

        if (!formData.category) {

            alert(
                "Category is required."
            );

            return;
        }

        if (!formData.assetId.trim()) {

            alert(
                "Asset ID / Reference is required."
            );

            return;
        }

        if (!formData.location) {

            alert(
                "Location is required."
            );

            return;
        }

        // ==========================================================
        // MAINTENANCE VALIDATION
        // ==========================================================

        if (
            formData.requiresRoutineMaint ===
                "Yes" &&
            !formData.maintenanceFrequency
        ) {

            alert(
                "Maintenance Frequency is required."
            );

            return;
        }

        // ==========================================================
        // PAT VALIDATION
        // ==========================================================

        if (
            formData.requiresPat === "Yes" &&
            !formData.patFrequency
        ) {

            alert(
                "PAT Test Frequency is required."
            );

            return;
        }

        // ==========================================================
        // DECOMMISSION VALIDATION
        // ==========================================================

        if (
            formData.decommissioned ===
                "Yes" &&
            !formData.decommissionDate
        ) {

            alert(
                "Decommission Date is required."
            );

            return;
        }

        try {

            console.log(
                "Submitting Equipment:",
                formData
            );

            // ======================================================
            // PHOTO INFORMATION
            // ======================================================

            if (formData.photoFile) {

                console.log(
                    "Selected photo:",
                    formData.photoFile
                );

                console.log(
                    "Photo name:",
                    formData.photoFile.name
                );

                console.log(
                    "Photo type:",
                    formData.photoFile.type
                );

                console.log(
                    "Photo size:",
                    formData.photoFile.size
                );
            }

            // ======================================================
            // CREATE EQUIPMENT
            // ======================================================

            const createdEquipment =
                await dispatch(
                    createEquipment(formData)
                ).unwrap();

            console.log(
                "Equipment created successfully:",
                createdEquipment
            );

            // ======================================================
            // RESET FORM
            // ======================================================

            resetForm();

            // ======================================================
            // NAVIGATE
            // ======================================================

            navigate("/equipment");

        } catch (submitError) {

            console.error(
                "Equipment submission failed:",
                submitError
            );
        }
    };

    // ============================================================
    // CANCEL
    // ============================================================

    const handleCancel = () => {

        dispatch(
            resetCreateEquipmentState()
        );

        resetForm();

        navigate("/equipment");
    };

    // ============================================================
    // CLEAR ERROR
    // ============================================================

    const clearError = () => {

        dispatch(
            clearEquipmentErrors()
        );
    };

    // ============================================================
    // CONTEXT VALUE
    // ============================================================

    const value = {

        formData,

        setFormData,

        updateField,

        updateComplianceField,

        resetForm,

        handleFillFakeData,

        handleSubmit,

        handleCancel,

        clearError,

        creating,

        createSuccess,

        error,
    };

    // ============================================================
    // PROVIDER
    // ============================================================

    return (
        <FormContext.Provider
            value={value}
        >
            {children}
        </FormContext.Provider>
    );
};

// ============================================================
// CUSTOM HOOK
// ============================================================

export const useEquipmentForm = () => {

    const context =
        useContext(
            FormContext
        );

    if (!context) {

        throw new Error(
            "useEquipmentForm must be used inside EquipmentFormProvider"
        );
    }

    return context;
};

// ============================================================
// DEFAULT EXPORT
// ============================================================

export default FormContext;